import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Avatar } from '@/components/ui';
import { useActividadReciente } from '@/hooks/queries/useDashboard';
import { ListWidgetSkeleton, ListWidgetError } from './WidgetSkeleton';
import { formatRelativeTime } from '@/lib/utils';
import { Ticket, MessageSquare, CheckSquare, Users, ArrowRight } from 'lucide-react';
import type { ActividadReciente } from '@/services/dashboard';

const TIPO_CONFIG: Record<string, { icon: typeof Ticket; color: string; path: string }> = {
  TICKET: { icon: Ticket, color: 'text-blue-600', path: '/tickets' },
  CONVERSACION: { icon: MessageSquare, color: 'text-green-600', path: '/conversaciones' },
  ACTIVIDAD: { icon: CheckSquare, color: 'text-purple-600', path: '/actividades' },
  CLIENTE: { icon: Users, color: 'text-cyan-600', path: '/clientes' },
};

export function ActividadRecienteWidget() {
  const { data, isLoading, isError } = useActividadReciente(5);

  if (isLoading) return <ListWidgetSkeleton count={5} />;
  if (isError || !data) return <ListWidgetError message="Error al cargar actividad reciente" />;

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Actividad Reciente</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 text-center py-8">
            No hay actividad reciente
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Actividad Reciente</CardTitle>
        <Link
          to="/actividades"
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          Ver todo
          <ArrowRight className="h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.map((item: ActividadReciente) => {
            const config = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.TICKET;
            const Icon = config.icon;

            return (
              <div key={item.id} className="flex items-start gap-3">
                <Avatar
                  src={item.usuario.avatar_url}
                  fallback={item.usuario.nombre}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 line-clamp-2">
                    {item.descripcion}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Icon className={`h-3 w-3 ${config.color}`} />
                    <span className="text-xs text-gray-500">
                      {item.usuario.nombre}
                    </span>
                    <span className="text-xs text-gray-400">&bull;</span>
                    <span className="text-xs text-gray-500">
                      {formatRelativeTime(item.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
